/**
 * 게임 핵심 수치 합성 — 순수 함수 (Phaser/BigNumber 비의존, 단위 테스트 대상).
 *
 * GameScene이 컨트롤러(statUpgrade / rebirthUpgrade / relic)에서 효과값을 뽑아 본 함수들로 결합한다.
 * 분리 목적: 합성 로직(가산·곱·클램프 순서)을 GameScene 인스턴스(Phaser 씬) 없이 검증하기 위함.
 * 골드 모듈(greed) 배수만 number로 합류하고, 큰 수 경계(BigNumber)는 호출자(GameScene)가 처리한다.
 */

/**
 * 기본 피해 계수 = (기본값 + 환생 공격력 강화 + 골드 공격력 강화) × 유물 공격 배수.
 * GameScene.getBaseDamage가 결과를 BigNumber.fromNumber로 감싼다. 가산이 먼저, 유물 배수는 곱.
 */
export function composeBaseDamage(
  baseDamage: number,
  rebirthAttackBonus: number,
  statAttackBonus: number,
  relicAttackMultiplier: number,
): number {
  return (baseDamage + rebirthAttackBonus + statAttackBonus) * relicAttackMultiplier;
}

/**
 * 발사 간격(ms) = max(minIntervalMs, (기본 - 골드 fireRate 감소 - 환생 fireRate 감소) × 유물 쿨타임 배수).
 * 두 감소량이 별개로 합산되고, 유물 쿨타임 배수는 곱(<1 = 감소, 호출부 relic.fireRateMultiplier가 max(0.1, 1-percent/100) 보장), 마지막에 하한 클램프.
 */
export function composeFireIntervalMs(
  baseIntervalMs: number,
  statReductionMs: number,
  rebirthReductionMs: number,
  relicFireRateMultiplier: number,
  minIntervalMs: number,
): number {
  return Math.max(
    minIntervalMs,
    (baseIntervalMs - statReductionMs - rebirthReductionMs) * relicFireRateMultiplier,
  );
}

/**
 * 골드 획득 배수 = 스탯 × 환생 × 유물 × greed(직접 타격 골드 배수). 모두 곱연산.
 * greedMultiplier는 탐욕 모듈의 처치 골드 배수(직접 타격만 1 초과, 범위/연쇄는 1).
 */
export function composeGoldMultiplier(
  statGoldMultiplier: number,
  rebirthGoldMultiplier: number,
  relicGoldMultiplier: number,
  greedMultiplier: number,
): number {
  return statGoldMultiplier * rebirthGoldMultiplier * relicGoldMultiplier * greedMultiplier;
}

/**
 * 치명타 확률 = (모듈 critChance + 스탯 강화 + 환생 강화 + 런처 기본)를 합산 후 0~1 클램프.
 * 모듈 critChance는 발사체 spec(crit 모듈)이 가진 값, 스탯/환생은 영구 강화 누적, 런처 기본은
 * 시작점(LAUNCHER) 고유 치명타(경로 무관 항상 가산 — 빈 그리드에서도 적용). 모두 가산.
 * rollCrit이 rng >= critChance로 판정하므로 상한 1(=100%)로 클램프한다(유물엔 crit 효과 없어 미합류).
 * launcherBaseCritChance는 기본 0이라 미전달 호출부(테스트/표시 등) 동작 불변.
 */
export function composeCritChance(
  moduleCritChance: number,
  statCritBonus: number,
  rebirthCritBonus: number,
  launcherBaseCritChance = 0,
): number {
  return Math.min(1, moduleCritChance + statCritBonus + rebirthCritBonus + launcherBaseCritChance);
}

/**
 * 크리티컬 데미지 배수 = max(기본 배수, 모듈 critMul) + 스탯 강화 + 환생 강화.
 * 기본 배수는 global.tsv critMultiplier(2 = 기본 2배 — 스탯 강화만으로 발동하는 crit의 바닥값).
 * 모듈 critMul은 등급별로 기본을 초과한다(crit legendary=3·transcendent=4, modules.tsv) — 모듈 apply가
 * 채운 spec.critMultiplier를 base와 max 합류해야 등급 상승분이 보존된다(과거엔 base로 덮어써 소실 — 회귀 fix).
 * CombatSystem.buildSpec이 발사체 spec.critMultiplier에 합성 → rollCrit이 isCrit 시 damageMultiplier × critMultiplier.
 */
export function composeCritMultiplier(
  baseCritMultiplier: number,
  moduleCritMultiplier: number,
  statCritDamageBonus: number,
  rebirthCritDamageBonus: number,
): number {
  return (
    Math.max(baseCritMultiplier, moduleCritMultiplier) + statCritDamageBonus + rebirthCritDamageBonus
  );
}
